import Bugsnag from '@bugsnag/js';
import { ReactComponent as ClearIcon } from 'assets/icons/icon-close.svg';
import { ReactComponent as BankIcon } from 'assets/icons/icon-library.svg';
import Button from 'components/Button';
import Card from 'components/Card/Card';
import DangerButton from 'components/DangerButton/DangerButton';
import ErrorMessage from 'components/ErrorMessage/ErrorMessage';
import Image from 'components/Image';
import Input from 'components/Input';
import Page from 'components/Page';
import S3Image from 'components/S3Image';
import { useFetchBankQuery } from 'graphql/queries/FetchBank.generated';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'routeMap';
import { useWrappedUrqlMutation } from 'toolbox';
import Flex from 'toolbox/src/components/Flex/Flex';
import HeroIconExclamation from 'toolbox/src/components/HeroIcons/HeroIconExclamation';
import Stack from 'toolbox/src/components/Stack/Stack';
import { useAddBankMutation } from './AddBank.generated';
import BankFactsheets from './BankFactsheets';
import BankImageUploader from './BankImageUploader';
import BankOrgPermissions from './BankOrgPermissions';
import BankRiskCard from './BankRiskCard';
import { useDeleteBankMutation } from './DeleteBank.generated';
import { useUpdateBankMutation } from './UpdateBank.generated';

const Bank = ({ id }) => {
  const [result] = useFetchBankQuery({ variables: { bank_id: id } });
  if (result?.error) Bugsnag.notify(result?.error);

  const bank = result.data?.banks_by_pk;

  const navigate = useNavigate();

  const [formData, setFormData] = useState<typeof bank>(bank);

  useEffect(() => {
    setFormData(bank);
  }, [bank]);

  const handleChange = (property, newValue) =>
    setFormData({ ...formData, [property]: newValue });

  const addBank = useWrappedUrqlMutation(useAddBankMutation, {
    onSuccess: (result) => {
      navigate.viewBankById({
        params: { id: result.insert_banks.returning?.[0].id },
      });
    },
    onError: () => {},
  });

  const updateBank = useWrappedUrqlMutation(useUpdateBankMutation);

  const saveChanges = async () => {
    try {
      if (!id) {
        await addBank.dispatch({
          name: formData?.name,
          address: formData?.address,
          regulator: formData?.regulator,
          ownership: formData?.ownership,
          notes: formData?.notes,
        });
      } else
        updateBank.dispatch({
          bank_id: formData?.id,
          modification: {
            name: formData?.name,
            address: formData?.address,
            regulator: formData?.regulator,
            ownership: formData?.ownership,
            notes: formData?.notes,
          },
        });
    } catch (err) {
      console.warn(err);
    }
  };

  const removeBankImage = async () => {
    try {
      const updateBankResult = await updateBank.dispatch({
        bank_id: formData?.id,
        modification: { image_upload_id: null },
      });
      console.log(updateBankResult);
    } catch (err) {
      console.warn(err);
    }
  };

  const undoChanges = () => setFormData(bank);

  const deleteBank = useWrappedUrqlMutation(useDeleteBankMutation, {
    onSuccess: () => {
      navigate.viewBanks();
      toast('Deleted bank successfully');
    },
  });

  if (id && !formData) {
    return null;
  }

  return (
    <Page
      title={bank?.name || 'Add New Bank'}
      icon={<BankIcon className="inline w-10 h-10 mr-2 -mb-1 text-gray-500" />}
    >
      <div className="card">
        <div className="cardBody">
          <aside>
            <h2 className="mb-6 text-lg font-semibold text-gray-800">
              Bank Info
            </h2>

            {bank?.image?.s3_key && (
              <div className="relative flex items-center mb-6 overflow-hidden bg-white rounded-lg">
                <ClearIcon
                  className="absolute top-0 right-0 w-6 h-6 mt-1 mr-1 text-red-500 bg-red-100 border border-red-400 rounded-full cursor-pointer hover:text-white hover:bg-red-600"
                  onClick={() => removeBankImage()}
                />
                {bank?.image?.public_url ? (
                  <Image src={bank?.image?.public_url} className="" />
                ) : (
                  <S3Image s3_key={bank?.image?.s3_key} className="" />
                )}
              </div>
            )}

            {id && <BankImageUploader bank_id={formData?.id} />}
          </aside>
          <main>
            <Input
              label="Name"
              value={formData?.name}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleChange('name', e.target.value)
              }
            />
            <Input
              label="Corporate Address"
              value={formData?.address}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleChange('address', e.target.value)
              }
            />

            <Input
              label="Local Regulator"
              value={formData?.regulator}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleChange('regulator', e.target.value)
              }
            />

            <Input
              label="Ownership"
              value={formData?.ownership}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleChange('ownership', e.target.value)
              }
            />

            <div className="input-wrapper">
              <label>Additional Information</label>
              <textarea
                name="message"
                className="w-full h-32 p-3"
                value={formData?.notes || ''}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                  handleChange('notes', e.target.value)
                }
              />
            </div>
          </main>
        </div>
        <footer className="flex justify-end w-full mt-6 bg-gray-100 rounded-b-lg">
          <Button
            onClick={undoChanges}
            className="mr-3 btn btn-white"
            disabled={JSON.stringify(formData) === JSON.stringify(bank)}
          >
            Undo Changes
          </Button>
          <Button
            onClick={saveChanges}
            className="btn"
            disabled={
              JSON.stringify(formData) === JSON.stringify(bank) ||
              !formData?.name
            }
          >
            Save
          </Button>
        </footer>
      </div>

      {id && <BankRiskCard bank={bank} />}
      {id && <BankFactsheets bank_id={bank?.id} />}
      {id && <BankOrgPermissions bank={bank} />}

      {id && (
        <Card title={`Delete ${bank.name}`}>
          <p className="mb-4 text-sm text-gray-600">
            Delete this bank permanently. This action is irreversible.
          </p>
          <Stack alignItems="flex-end">
            <DangerButton onClick={() => deleteBank.dispatch({ id })}>
              <Flex mx={2}>
                <HeroIconExclamation />
                <p>Delete Bank</p>
              </Flex>
            </DangerButton>
            <ErrorMessage {...deleteBank} />
          </Stack>
        </Card>
      )}

      <div className="h-32" />
    </Page>
  );
};

export const NoBank = () => (
  <Page title="">
    <div className="absolute inset-0 flex flex-col items-center justify-center">
      <BankIcon className="w-full h-auto max-w-sm text-gray-400" />
      <h2 className="text-2xl font-semibold text-gray-400">No Bank Selected</h2>
    </div>
  </Page>
);

export default Bank;
